import { type InfiniteData, infiniteQueryOptions, type QueryClient, queryOptions } from '@tanstack/react-query'
import type { PageCaseRow } from '../../lib/api/generated'
import type { CaseListParams, QueueApi } from './api'
import { generatedQueueApi } from './api'
import { type ApiLane, type NeedsUsFilter, QUEUE_LANES, type QueueLaneSort, type QueueRouteLane } from './lanes'

let testOverride: QueueApi | null = null
let testDefault: QueueApi | null = null

function api(): QueueApi {
  return testOverride ?? testDefault ?? generatedQueueApi
}

/** src/test/setup.ts installs the fixture queue as the default for every vitest run. */
export function installQueueTestDefault(fixture: QueueApi): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Queue API replacement is test-only')
  testDefault = fixture
}

/**
 * Tests in this feature replace the boundary; production can only use the generated SDK. The test fixture is
 * installed from src/test/setup.ts, so its bytes never reach the production bundle (#67 slice 15).
 */
export function setQueueApiForTests(replacement: QueueApi | null): void {
  if (import.meta.env.MODE !== 'test') throw new Error('Queue API replacement is test-only')
  testOverride = replacement
}

/** A trailing `brand` argument only when one is sent, so an unscoped call is exactly today's call. */
export function brandArg(brand: string | undefined): [string] | [] {
  return brand ? [brand] : []
}

export const queueKeys = {
  all: ['queue'] as const,
  lanes: ['queue', 'lanes'] as const,
  /** Brand-scoped keys extend today's keys only when a `?brand=` is sent, so an unscoped read keeps today's key. */
  lanesFor: (brand?: string) => (brand ? (['queue', 'lanes', brand] as const) : (['queue', 'lanes'] as const)),
  needsBase: ['queue', 'needs-us-now'] as const,
  needs: (filter: NeedsUsFilter, brand?: string) =>
    brand ? (['queue', 'needs-us-now', filter, brand] as const) : (['queue', 'needs-us-now', filter] as const),
  laneBase: (lane: ApiLane) => ['queue', lane] as const,
  lane: (lane: ApiLane, sort: QueueLaneSort = 'who_owes', brand?: string) =>
    brand ? (['queue', lane, sort, brand] as const) : (['queue', lane, sort] as const),
  cases: (params: CaseListParams, brand?: string) =>
    brand ? (['queue', 'cases', params, brand] as const) : (['queue', 'cases', params] as const),
  parked: ['queue', 'parked'] as const,
  expansion: (caseId: string) => ['queue', 'case', caseId, 'expansion'] as const,
}

export const queueLanesQuery = (brand?: string) =>
  queryOptions({
    queryKey: queueKeys.lanesFor(brand),
    queryFn: ({ signal }) => api().lanes(signal, ...brandArg(brand)),
    staleTime: 30_000,
    retry: 1,
  })

export const queueParkedQuery = () =>
  queryOptions({
    queryKey: queueKeys.parked,
    queryFn: ({ signal }) => api().parked(signal),
    staleTime: 30_000,
    retry: 1,
  })

const MAX_TIMER_MS = 2_147_000_000

/**
 * Arm exactly one refresh at the first server-computed event-less lane boundary (#694). Very distant boundaries
 * are re-armed below the browser timeout ceiling; this never turns into interval polling.
 */
export function armQueueBoundaryRefresh(
  nextRefreshAt: string | null | undefined,
  refresh: () => void,
  now: () => number = Date.now,
): () => void {
  const boundary = nextRefreshAt ? Date.parse(nextRefreshAt) : Number.NaN
  if (!Number.isFinite(boundary)) return () => undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let cancelled = false
  const arm = () => {
    if (cancelled) return
    const remaining = boundary - now()
    if (remaining <= 0) {
      refresh()
      return
    }
    const delay = remaining + 50
    timer = setTimeout(delay > MAX_TIMER_MS ? arm : refresh, Math.min(delay, MAX_TIMER_MS))
  }
  arm()
  return () => {
    cancelled = true
    if (timer !== undefined) clearTimeout(timer)
  }
}

export const queueLaneQuery = (lane: ApiLane, sort: QueueLaneSort, brand?: string) =>
  infiniteQueryOptions({
    queryKey: queueKeys.lane(lane, sort, brand),
    queryFn: ({ pageParam, signal }) => api().laneRows(lane, pageParam, signal, sort, ...brandArg(brand)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: 1,
  })

export const queueNeedsUsNowQuery = (filter: NeedsUsFilter, brand?: string) =>
  infiniteQueryOptions({
    queryKey: queueKeys.needs(filter, brand),
    queryFn: ({ pageParam, signal }) => api().needsUsNow(filter, pageParam, signal, ...brandArg(brand)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: 1,
  })

export const caseListQuery = (params: CaseListParams, brand?: string) =>
  infiniteQueryOptions({
    queryKey: queueKeys.cases(params, brand),
    queryFn: ({ pageParam, signal }) => api().caseList(params, pageParam, signal, ...brandArg(brand)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 30_000,
    retry: 1,
  })

export const queueExpansionQuery = (caseId: string, enabled: boolean) =>
  queryOptions({
    queryKey: queueKeys.expansion(caseId),
    queryFn: ({ signal }) => api().expansion(caseId, signal),
    enabled,
    staleTime: 15_000,
    retry: false,
  })

export function queueApi(): QueueApi {
  return api()
}

/**
 * The loaded queue lane a case sits in, from the Query cache only (no fetch): the case view's j/k and Esc use it.
 * `id` is a case number (C-#####) or UUID. Null when no loaded lane holds the case.
 */
export function queueSiblingsOf(queryClient: QueryClient, id: string): { lane: QueueRouteLane; numbers: string[]; index: number } | null {
  for (const [, data] of queryClient.getQueriesData<InfiniteData<PageCaseRow>>({ queryKey: queueKeys.needsBase })) {
    const rows = data?.pages?.flatMap((page) => page.items) ?? []
    const index = rows.findIndex((row) => row.number === id || row.id === id)
    if (index >= 0) return { lane: 'needs-us-now', numbers: rows.map((row) => row.number), index }
  }
  for (const def of QUEUE_LANES) {
    for (const [, data] of queryClient.getQueriesData<InfiniteData<PageCaseRow>>({ queryKey: queueKeys.laneBase(def.api) })) {
      const rows = data?.pages?.flatMap((page) => page.items) ?? []
      const index = rows.findIndex((row) => row.number === id || row.id === id)
      if (index >= 0) return { lane: def.route as QueueRouteLane, numbers: rows.map((row) => row.number), index }
    }
  }
  return null
}
